import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { FormControl } from '@angular/forms';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { SearchService } from '../../services/search/search.service';
import { StorageService } from '../../services/storage/storage.service';
import {
  AuthService,
  IllGuestInstructions,
  UserData,
  SYSTEMCATEGORY,
} from '../../services/auth/auth.service';
import { SharedService } from '../../services/shared/shared.service';
import { RequestService } from './../request/request.service';
import {
  Page,
  PredefinedTemplatesService,
} from './../../services/edit/predefined-templates.service';
import { OpacSettingsService } from '../../services/opac-settings/opac-settings.service';
import { NotifyService } from '../../components/side-nav/notify/notify.service';
import { ScriptsService } from './../../services/scripts/scripts.service';
import { YourListService } from '../your-list/your-list.service';
import { RoutingService } from '../../services/routing/routing.service';
import { SearchIndexes, TargetGroups } from '../../core/interfaces/search.interface';
import { QuickMenuService } from '../../components/side-nav/quick-menu/quick-menu.service';
import { LayoutService, SideNavConfig } from '../../services/layout.service';
import * as $ from 'jquery';
import { Overlay } from '@angular/cdk/overlay';
import { MenuItem } from '../../layouts/main-v6-layout/main-v6-layout.component';
import { AnalyticsService } from '../../services/analytics/analytics.service';

declare let gtag;

@Component({
  selector: 'ag-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('header') header: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @Input() menuItems: MenuItem[];
  @Output() tracking = new EventEmitter<IllGuestInstructions>();

  readonly SYSTEM_CATEGORY = SYSTEMCATEGORY;
  searchTerm = new FormControl('');
  unreadMessageCount$: Observable<number> = this._notifyService.unreadMessageCount$;

  avatar: string;

  indexesArray: any[];
  selectedIndex: any = {};
  userData: UserData;
  showGuestTracking: any = false;
  showResourceHasFocus = false;
  selectSearchIndexHasFocus = false;
  searchInputHasFocus = false;
  searchAdvancedHasFocus = false;
  webLinksOptions: any;
  showBrowseIndexes: boolean;
  sidenavOpened = false;
  elementRef: ElementRef;

  showRightActions = false;
  showSearchOptions = false;
  isSearchOptionsOpen = false;

  quickMenu: any;
  showList: any = false;
  simpleSearchOptions: any;
  showIndexSelection = true;
  showResourceSelection = false;
  defaultScopingLevel = 0;
  enableSearch = true;
  searchResultsOptions: any;

  searchSuggestions = [];

  yourListCount: number;
  yourSharedListCount: number;
  resources: number[] = [];

  private _searchTermSubscription$: Subscription;
  private _destroyed$ = new Subject<void>();

  constructor(
    private _router: Router,
    public overlay: Overlay,
    public viewContainerRef: ViewContainerRef,
    public layoutService: LayoutService,
    private _notifyService: NotifyService,
    private _opacSettingsService: OpacSettingsService,
    private _predefinedTemplatesService: PredefinedTemplatesService,
    private _requestService: RequestService,
    public routingService: RoutingService,
    private _quickMenuService: QuickMenuService,
    private _scriptsService: ScriptsService,
    private _searchService: SearchService,
    public sharedService: SharedService,
    private _yourListService: YourListService,
    public authService: AuthService,
    public dialog: MatDialog,
    public myElement: ElementRef,
    public storageService: StorageService,
    public snackBar: MatSnackBar,
    private _analyticsService: AnalyticsService,
  ) {
    this._loadScriptsStylesLazy();
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    // this.layoutService.setHeaderHeight(this.header.nativeElement.offsetHeight);
  }

  ngOnInit() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this._hideSearchSuggestions();
      }
    });

    this._searchService
      .getQuickSearchResourcesBS()
      .subscribe((selectedResources: TargetGroups[]) => {
        this.resources = SearchService.getSelectResources(selectedResources);
      });

    this.sharedService
      .getLibraryChangedBS()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((values) => {
        if (!values) {
          return;
        }
        //TODO Probably does not belong here, returns nothing sets nothings
        this._predefinedTemplatesService.getLibAndBindInfoAsync(values.cid, values.lid);
      });

    combineLatest([this.sharedService.userData$, this.sharedService.getOpacResultsBS()]).subscribe(
      (data) => {
        if (!data[0]) {
          return;
        }

        this._setupUserDataOptions(data[0]);

        if (this.userData.isGuest) {
          this.searchTerm.patchValue('');
        }

        if (!data[1]) {
          return;
        }

        const opacResults = data[1];
        const showBrowseIndexesPatronGuest =
          !this.userData.isStaff &&
          opacResults.showBrowseIndexesDisplay.patronGuest?.find((x) => x.selected).value == 'true';
        const showBrowseIndexesStaff =
          this.userData.isStaff &&
          opacResults.showBrowseIndexesDisplay.staff.find((x) => x.selected).value == 'true';
        this.showBrowseIndexes = showBrowseIndexesPatronGuest || showBrowseIndexesStaff;
      },
    );

    this._yourListService.lists$.pipe(takeUntil(this._destroyed$)).subscribe((list) => {
      this.yourListCount = list?.numOfLists ?? 0;
      this.yourSharedListCount = list?.shared.length ?? 0;
    });

    this._searchService
      .getSearchIndexesBS()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((indexes: SearchIndexes) => {
        if (!indexes) {
          return;
        }

        this.indexesArray = indexes.simpleSearchIndexes;
        this.selectedIndex = this.indexesArray.find((x) => x.isDefault);
      });

    this.sharedService.openSideNavSettigs$.pipe(takeUntil(this._destroyed$)).subscribe((value) => {
      if (!value) {
        return;
      }

      this.openSideNavMenu('start', 'page');
      this.sharedService.setOpenSideNavSettings(null);
    });

    this._notifyService
      .getOpenNotSideNavBS()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((flag) => {
        if (!flag) {
          return;
        }

        if (this.userData?.isStaff || this.userData?.isPatron) {
          this.openSideNavMenu('end', 'notify');
        }
      });

    this._quickMenuService.quickMenuItems$.pipe(takeUntil(this._destroyed$)).subscribe((data) => {
      if (data) {
        this.quickMenu = data;
      }
    });

    this._opacSettingsService
      .getWebLinkOptionsBS()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((webLinksOptions) => {
        if (webLinksOptions) {
          this.webLinksOptions = webLinksOptions;
        }
      });
  }

  ngAfterViewInit() {
    if (this.header.nativeElement.offsetHeight !== 0) {
      this.layoutService.setHeaderHeight(this.header.nativeElement.offsetHeight);
    }

    const observer = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      this.layoutService.setHeaderHeight(height);
    });
    observer.observe(this.header.nativeElement);

    $('input.deletable')
      .wrap('<span class="deleteicon" />')
      .after(
        $('<span/>').click(function () {
          $(this).prev('input').val('').trigger('change').focus();
        }),
      );
  }

  /**
   * Creates and subscribes to the search suggestion subscription.
   */
  private _initializeSearchSuggestions(): void {
    if (this.simpleSearchOptions?.showTypeAhead) {
      this._searchTermSubscription$ = this.searchTerm.valueChanges
        .pipe(
          debounceTime(250),
          map((searchTerm: string) => searchTerm.trim()),
          distinctUntilChanged(),
          switchMap((queryTerm) => {
            if (!queryTerm) {
              return of([]);
            } else {
              return this._searchService.suggestSearchResults(queryTerm);
            }
          }),
        )
        .subscribe((suggestions) => {
          this.searchSuggestions = suggestions ?? [];
          if (this.searchSuggestions.length > 0) {
            this._showSearchSuggestions();
          } else {
            this._hideSearchSuggestions();
          }
        });
    }
  }

  /**
   * Resets the search suggestion subscription.
   */
  private _resetSearchSuggestions() {
    if (this._searchTermSubscription$) {
      this._searchTermSubscription$.unsubscribe();
    }
    this._initializeSearchSuggestions();
  }

  private _showSearchSuggestions() {
    this.searchInput?.nativeElement.setAttribute('spellcheck', 'false');
  }

  private _hideSearchSuggestions() {
    this.searchSuggestions = [];
    this.searchInput?.nativeElement.setAttribute('spellcheck', 'true');
  }

  private _loadScriptsStylesLazy(): void {
    this._scriptsService.loadExternalStyles(
      'https://cdn.datatables.net/1.10.19/css/jquery.dataTables.min.css',
    );
  }

  clearInput() {
    this.searchInput.nativeElement.value = '';
    this.searchInput.nativeElement.blur();
    this.searchInput.nativeElement.focus();
    this.searchTerm.setValue('');
  }

  //------------Page Actions--------------------//
  goToYourList() {
    if (this.authService.isGuest()) {
      this.layoutService.openSideNav({
        template: 'addList',
        position: 'end',
        data: true,
      });
    } else {
      this._router.navigateByUrl('/list');
    }
  }

  goToLogin() {
    this.routingService.goToLogin(this.routingService.currentUrl);
  }

  aboutMeKC() {
    this.openSideNavMenu('end', '');
  }

  exitKC() {
    //only place that is set
    this.sharedService.setKidsCat(false);
    this._router.navigate(['/home'], {
      queryParams: { cid: this.userData.customerId, lid: this.userData.libraryId },
    });
  }

  goToPrevKC() {
    const uri = this.storageService.getItem('previousUrl');
    if (uri) {
      this._router.navigateByUrl(uri);
    }
  }

  guestTrack() {
    this.authService.getIllGuestInstructions().subscribe(
      (response) => {
        this.tracking.emit(response);
      },
      (error: unknown) => {
        if (error instanceof Error) {
          console.error(error.message);
        }
        this.sharedService.openSnackBar('Unable to get Ill tracking instructions.');
      },
    );
  }

  mobileSelectIndex(index) {
    this.onChangeSearchIndex(index);
    this.searchInput.nativeElement.focus();
  }
  // ----------------------------------- index change detection -------------------
  // https://stackoverflow.com/questions/33700266/how-can-i-get-new-selection-in-select-in-angular-2
  onChangeSearchIndex(event) {
    this.selectedIndex = this.indexesArray.find(
      (i) => i.value == event.value && i.name == event.name,
    );
  }

  private _setupUserDataOptions(userData) {
    this.userData = userData;
    this.showGuestTracking = this.userData.illOptions.showGuestTracking;
    const opacOptions = this.userData.opacOptions;
    this.enableSearch = opacOptions.enableSearch;
    this.simpleSearchOptions = opacOptions.simpleSearchOptions;
    this.showIndexSelection = this.simpleSearchOptions.showIndexSelection;
    this.showResourceSelection = this.simpleSearchOptions.showResourceSelection;
    this.showList = this.authService.hasDisplayYourListPermission();

    this._resetSearchSuggestions();
  }

  _goToDashboard() {
    this._predefinedTemplatesService.addRemoveCurrentClass();
    $('.pages7 a').addClass('current-route');

    //TODO Calling this with a staff page open, leaves the title as the staff page
    //We need to figure out how to call dashboardService.closeStaff() before routing
    this.routingService.goToDashboard();
  }

  // https://stackoverflow.com/questions/47629024/angular-material-2-autocomplete-mat-option-element-doesnt-trigger-keyup-event
  // https://stackoverflow.com/questions/42427928/material-2-autocomplete-select-option
  selectSearchOption(event, value) {
    if (event.source && value) {
      this.doSearch(value.searchTerm, value);
    }
  }

  searchEnter() {
    this.doSearch(this.searchTerm.value);
  }

  scrollToOption(id) {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    this.sharedService.scrollIntoView(element);
  }

  handleMenuNavigation(menuItem: MenuItem) {
    switch (menuItem.type) {
      case 'home':
        this._goToHomePage();
        break;
      case 'staff-dashboard':
        this._goToDashboard();
        break;
      case 'search-history':
        this.openSideNavMenu('start', 'history');
        break;
      case 'blank-ill-request':
        this._goToBlankILLRequest();
        break;
      case 'web-links':
        this._goToWebLinks();
        break;
      case 'page':
        this._goToPage(menuItem.page);
    }
  }

  // -------------------------- home page routing -------------------------------------
  private _goToHomePage() {
    const obj = {
      type: 'primary page',
      index: -1,
    };
    this._predefinedTemplatesService.underlineActivePage(obj);
    this._router.navigate(['/home'], {
      queryParams: { cid: this.userData.customerId, lid: this.userData.libraryId },
    });
  }

  // --------------------------------- go to web links -------------------------
  private _goToWebLinks() {
    this._predefinedTemplatesService.addRemoveCurrentClass();
    $('.pages9 a').addClass('current-route');
    this._router.navigateByUrl('/webLinks');
  }

  private _goToPage(page: Page) {
    this.routingService.goToPage(page);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------hot keys user exp -----------------------------
  //   ---------------------------------------------------------------------------

  closeOverlay() {
    $('#search-overlay').css('display', 'none');
  }

  goToAdvancedSearch() {
    this._router.navigate(['/advancedsearch'], {
      queryParams: {
        history: false,
      },
    });
  }

  goToBrowseSearch() {
    const subscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.storageService.has('BrowseSearchData')) {
          this.storageService.remove('BrowseSearchData');
        }
        if (this.storageService.has('BrowseTerms')) {
          this.storageService.remove('BrowseTerms');
        }
        if (this.storageService.has('BrowseSearch')) {
          this.storageService.remove('BrowseSearch');
        }
        if (this.storageService.has('BrowseInitialSearchOffset')) {
          this.storageService.remove('BrowseInitialSearchOffset');
        }
      }
    });
    this._router.navigate(['/browse']);
    subscription.unsubscribe();
  }

  _goToBlankILLRequest() {
    this._requestService.requestThisItemBlank();
  }

  // Account Details
  goToMyAccount() {
    if (this.authService.loginSuccess()) {
      this._router.navigate(['/account']);
    } else {
      alert('login first');
    }
  }

  /**
   * Permform the main search
   * @param value
   * @param content
   * @returns
   */
  doSearch(value, content = null) {
    if (!this.resources.length || !this.indexesArray?.length) {
      return;
    }

    this._resetSearchSuggestions();

    if (!this.enableSearch) {
      const message = 'You must login to the system before searching';
      this.openSnackBar(message, 'ok');
      return;
    }
    if (SharedService.isEmpty(value)) {
      const message = 'Please enter a search term';
      this.openSnackBar(message, 'ok');
      return;
    }
    const index = content ? content.index : this.selectedIndex.value;
    this._analyticsService.logEvent('Search', {
      index1: index,
      query1: value,
    });

    // Do not emit the event as it will trigger the search suggestions again.
    this.searchTerm.setValue(value, { emitEvent: false });

    this._searchService.doSearchSimple(
      index,
      value,
      this.resources,
      this.userData.opacOptions.simpleSearchOptions.defaultScopingLevel,
    );
    $('#staffClose').click();
  }

  // ----------------------------------------------------------------------
  //   ------------------------------USER EXPERIENCE --------------------------
  //   ------------------------------------------------------------------------

  openSnackBar(message: string, action: string = null) {
    this.snackBar.open(message, action, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  toggleSideNav() {
    const sideNavData: SideNavConfig = {
      position: 'start',
      template: '',
    };
    this.layoutService.toggleSideNav(sideNavData);
  }

  openSideNavMenu(pos: 'start' | 'end', template: string) {
    if (template == 'history') {
      this._predefinedTemplatesService.addRemoveCurrentClass();
      $('.pages10 a').addClass('current-route');
    }

    const menu = {
      position: pos,
      template: template,
    };
    this.layoutService.openSideNav(menu);
  }

  focus() {
    this.myElement.nativeElement.focus();
  }

  ngOnDestroy() {
    if (this._searchTermSubscription$) {
      this._searchTermSubscription$.unsubscribe();
    }
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
